import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MessageSquare, Users, BarChart3, Shield, Zap, ArrowRight, CheckCircle, LayoutDashboard, FileSpreadsheet, DollarSign, Mail, Code } from 'lucide-react';
import ChatWidget from '@/components/ChatWidget';
import { useAuth } from '@/context/AuthContext';

// LinkedIn Conversion Tracking Helper
const trackLinkedInConversion = () => {
  if (typeof window !== 'undefined' && window.lintrk) {
    window.lintrk('track', { conversion_id: 28515713 });
  }
};

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const features = [
    {
      icon: MessageSquare,
      title: 'Intake Chatbot',
      description: 'Conversational interface that captures all essential case details from potential clients 24/7.'
    },
    {
      icon: Zap,
      title: 'Lead Prioritization',
      description: 'Automatically organizes and prioritizes leads based on the information submitted.'
    },
    {
      icon: FileSpreadsheet,
      title: 'CMS Integration',
      description: 'Export leads directly to Clio, Filevine, and 20+ other legal case management systems.'
    },
    {
      icon: BarChart3,
      title: 'Attorney Dashboard',
      description: 'View, filter, and manage all leads in one place with real-time status updates.'
    },
    {
      icon: Shield,
      title: 'Secure Document Upload',
      description: 'Clients can securely upload photos, police reports, and medical records.'
    },
    {
      icon: Users,
      title: 'Lead Management',
      description: 'Track every lead from initial contact through conversion with detailed histories.'
    }
  ];

  const benefits = [
    'Capture leads 24/7 without missing a single inquiry',
    'Reduce intake staff workload by 70%',
    'Prioritize leads instantly with automatic scoring',
    'Never lose a lead to slow response times',
    'Embed on your website in minutes'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/pricing">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium" data-testid="pricing-nav-btn">
                  Pricing
                </Button>
              </Link>
              <Link to="/for-it-teams">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium" data-testid="for-it-nav-btn">
                  For IT
                </Button>
              </Link>
              <Link to="/chat-demo">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium" data-testid="demo-nav-btn">
                  Demo
                </Button>
              </Link>
              {user ? (
                /* Show Dashboard button for logged-in users */
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-teal-500 hover:bg-teal-600 text-white" 
                  data-testid="dashboard-nav-btn"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              ) : (
                /* Show Sign In / Get Started for logged-out users */
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium" data-testid="login-nav-btn">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register" onClick={trackLinkedInConversion}>
                    <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="get-started-nav-btn">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 hero-gradient relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1.3fr_0.7fr] gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                <Zap className="w-4 h-4" />
                Legal Intake Automation
              </div>
              <h1 
                className="text-4xl sm:text-5xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Turn Your Law Firm&apos;s Website Visitors Into
                <span className="text-teal-500"> Signed Clients</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                Automate your personal injury intake process with our 24/7 chatbot. 
                Capture leads, prioritize inquiries, and convert more clients while you focus on winning cases.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" onClick={trackLinkedInConversion}>
                  <Button 
                    size="lg" 
                    className="bg-teal-500 hover:bg-teal-600 text-white px-8 h-12 text-base font-semibold"
                    data-testid="hero-get-started-btn"
                  >
                    Start Free Trial
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 h-12 text-base font-semibold border-2 border-teal-500 text-teal-600 hover:bg-teal-50"
                    data-testid="hero-pricing-btn"
                  >
                    <DollarSign className="w-5 h-5 mr-2" />
                    View Pricing
                  </Button>
                </Link>
                <Link to="/chat-demo">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 h-12 text-base font-semibold border-2 border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="hero-demo-btn"
                  >
                    See Live Demo
                  </Button>
                </Link>
              </div>
              {/* Share with IT Callout Box */}
              <div className="bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-200 rounded-xl p-4 mt-2 max-w-xl">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Code className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-slate-900 font-semibold text-sm">Decision maker?</p>
                      <p className="text-slate-600 text-sm">Share with your IT team for easy setup</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <a 
                      href="mailto:?subject=Check%20out%20IntakePal%20-%20Client%20Intake%20Automation&body=Hi%2C%0A%0AI%20came%20across%20IntakePal%20and%20thought%20it%20could%20help%20automate%20our%20client%20intake%20process.%20Can%20you%20take%20a%20look%20at%20the%20implementation%20requirements%3F%0A%0AIT%20Implementation%20Guide%3A%20https%3A%2F%2Fintakepal.com%2Ffor-it-teams%0A%0AMain%20Website%3A%20https%3A%2F%2Fintakepal.com%0A%0AThanks!"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-sm font-medium transition-colors"
                      data-testid="share-with-it-btn"
                    >
                      <Mail className="w-4 h-4" />
                      Share Now
                    </a>
                    <Link 
                      to="/for-it-teams" 
                      className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium"
                      data-testid="it-guide-link"
                    >
                      Learn More
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              {/* Synthesia Video with Auto-play */}
              <div className="bg-white rounded-2xl shadow-2xl p-4 border border-slate-200 max-w-md mx-auto">
                <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '1920/1080', borderRadius: '0.5rem' }}>
                  <iframe 
                    src="https://share.synthesia.io/embeds/videos/915d89e7-31c2-43cb-858c-dec16baba160" 
                    loading="eager" 
                    title="Synthesia video player - Stop Losing Injury Leads" 
                    allowFullScreen 
                    allow="autoplay; encrypted-media; fullscreen; microphone; screen-wake-lock;" 
                    style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, border: 'none', padding: 0, margin: 0, overflow: 'hidden' }}
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">New Lead Received</p>
                    <p className="text-xs text-slate-500">Auto accident - High priority</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Why Law Firms Choose IntakePal
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {benefits.map((benefit) => (
              <div 
                key={benefit} 
                className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200"
              >
                <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                <span className="text-slate-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Everything You Need to Automate Intake
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A complete solution designed specifically for personal injury law firms
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <Card 
                key={feature.title} 
                className="feature-card border-slate-200 hover:border-teal-300 transition-all duration-300"
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 
                    className="text-xl font-semibold text-slate-900 mb-2"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-500 to-emerald-500">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium text-white mb-4">
                  <DollarSign className="w-4 h-4" />
                  Simple Pricing
                </div>
                <h2 
                  className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  $179.99<span className="text-xl font-normal text-teal-100">/month</span>
                </h2>
                <p className="text-teal-100 text-lg">
                  Everything included. Start with a 7-day free trial.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/pricing">
                  <Button 
                    size="lg" 
                    className="bg-white text-teal-600 hover:bg-teal-50 px-8 h-12 text-base font-semibold"
                    data-testid="pricing-teaser-details-btn"
                  >
                    See Full Details
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 h-12 text-base font-semibold"
                    data-testid="pricing-teaser-trial-btn"
                  >
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Ready to Transform Your Intake Process?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join hundreds of law firms already using IntakePal to capture more leads and convert more clients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-teal-500 hover:bg-teal-600 text-white px-8 h-12 text-base font-semibold"
                data-testid="cta-get-started-btn"
              >
                Start Your Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <p className="text-sm text-slate-500 mt-4">7-day free trial. Cancel anytime.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-teal-600">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-teal-600">
                Terms of Service
              </Link>
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} IntakePal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Chat Widget Demo */}
      <ChatWidget />
    </div>
  );
};

export default LandingPage;
